import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Shield, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AuthContext } from '../../contexts/AuthContext';
import { ApiApi, Register, TeamsApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { toast } from 'sonner';

const getClient = () => {
  return new ApiApi(getApiConfiguration());
};

const SignupAfterInvitation = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string[]}>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showVerificationLink, setShowVerificationLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { invitationId } = useParams();
    const { token } = useContext(AuthContext);
  const navigate = useNavigate();
    

  const teamsClient = new TeamsApi(getApiConfiguration(token));

  const fetchInvitation = async () => {
    console.log(invitationId)
    if (!invitationId) return;

    try {
      const response = await teamsClient.invitationDetailsRetrieve({
        invitationId: invitationId,
      });
      setEmail(response.email);
    } catch (err) {
      console.log('Error fetching invitation:', err);
      toast.error('Failed to load invitation');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors({});
    setGeneralErrors([]);

      const client = getClient();
      if(!invitationId || !email || !password || !confirmPassword || !firstName || !lastName) {
        setIsLoading(false);
        return;
      }

    try {
      // First register the user using the invitation
        const response = await client.apiTeamsInvitationSignupCreate({
            invitationId: invitationId,
            userSignup: {
                username: email,
                password1: password,
                password2: confirmPassword,
                firstName: firstName,
                lastName: lastName,
                email: email
            }
        });
        
      // Show success message without redirecting
      setSuccessMessage('Successfully Signed Up');
      setGeneralErrors([]);
      setFieldErrors({});
        setShowVerificationLink(false);
        navigate('/');
    } catch (error: any) {
      console.error('Error during signup/login:', error);
      setSuccessMessage('');
      
      if (error.response) {
        try {
          const errorData = await error.response.json();
          const newFieldErrors: {[key: string]: string[]} = {};
          const newGeneralErrors: string[] = [];

          if (typeof errorData === 'string') {
            newGeneralErrors.push(errorData);
          } else if (errorData.action === 'request_verification') {
            // Show verification link for unverified email
            newGeneralErrors.push(errorData.detail);
            setShowVerificationLink(true);
          } else {
            Object.entries(errorData).forEach(([key, errors]) => {
              toast.error(errors as string);
              const errorList = Array.isArray(errors) ? errors : [errors];
              if (key === 'email') {
                newFieldErrors[key] = errorList.map(err => `${err}`);
              } else if (key === 'detail') {
                errorList.forEach(err => {
                  newGeneralErrors.push(`${err}`);
                });
              }
            });
          }

          setFieldErrors(newFieldErrors);
          setGeneralErrors(newGeneralErrors);
        } catch (jsonError) {
          setGeneralErrors(["An unexpected error occurred. Please try again."]);
        }
      } else {
        setGeneralErrors(["Network error. Please check your connection and try again."]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (invitationId) {
      fetchInvitation();
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <Shield className="h-10 w-10 text-blue-500" />
            <h1 className="text-2xl font-bold text-white ml-3">Invitation Signup</h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 bg-gray-800/30 border ${
                    fieldErrors.email ? 'border-red-500' : 'border-gray-800'
                  } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Enter your email"
                  required
                  disabled={!!invitationId}
                />
              </div>
              {fieldErrors.email && (
                <div className="text-sm text-red-500">
                  {fieldErrors.email.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
            
            {/* First Name */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">First Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`w-full px-4 py-2 bg-gray-800/30 border ${fieldErrors.firstName ? 'border-red-500' : 'border-gray-800'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              {fieldErrors.firstName && (
                <div className="text-sm text-red-500">
                  {fieldErrors.firstName.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Last Name */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Last Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`w-full px-4 py-2 bg-gray-800/30 border ${fieldErrors.lastName ? 'border-red-500' : 'border-gray-800'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Enter your last name"
                  required
                />
              </div>
              {fieldErrors.lastName && (
                <div className="text-sm text-red-500">
                  {fieldErrors.lastName.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
                      
            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Password</label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-2 bg-gray-800/30 border ${fieldErrors.password1 ? 'border-red-500' : 'border-gray-800'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Enter your password"
                  required
                />
              </div>
              {fieldErrors.password1 && (
                <div className="text-sm text-red-500">
                  {fieldErrors.password1.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Confirm Password</label>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-2 bg-gray-800/30 border ${fieldErrors.password2 ? 'border-red-500' : 'border-gray-800'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              {fieldErrors.password2 && (
                <div className="text-sm text-red-500">
                  {fieldErrors.password2.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="text-sm text-green-500 bg-green-500/10 px-4 py-2 rounded-lg">
                {successMessage}
              </div>
            )}

            {/* General Errors */}
            {generalErrors.length > 0 && (
              <div className="text-sm text-red-500 bg-red-500/10 px-4 py-2 rounded-lg">
                {generalErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
                {showVerificationLink && (
                  <div className="mt-2">
                    <Link 
                      to="/resend-verification" 
                      className="text-blue-500 hover:text-blue-400 underline"
                      state={{ email }}
                    >
                      Click here to request verification email again
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <Button 
              className="w-full" 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>

          {/* Footer */}
          {/* <p className="text-sm text-gray-400 text-center mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-400">
              Sign in
            </Link>
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default SignupAfterInvitation;